<template>
  <v-card>
    <v-card-title>
      <span class="headline">{{ formTitle }}</span>
      <v-spacer></v-spacer>
      <v-switch v-model="user.isActive" label="Actif"></v-switch>
    </v-card-title>
    <v-card-text>
      <v-form>
        <v-container>
          <v-row dense>
            <template v-if="!admin">
              <v-col cols="12">
                <v-img class="mb-4" v-if="logoPreview" :src="logoPreview" height="100px" aspect-ratio="16/9" contain></v-img>
                <v-file-input
                  placeholder="Choisissez un logo"
                  prepend-icon="mdi-camera"
                  label="Logo"
                  v-model="logoFile"
                  ref="file"
                  accept="image/*"
                  outlined>
                </v-file-input>
              </v-col>
              <v-col cols="12">
                <v-text-field
                  v-model="user.brand"
                  :label="$t('Users.Form.brand')"
                  :error-messages="brandErrors"
                  prepend-inner-icon="store"
                ></v-text-field>
              </v-col>
            </template>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="user.email"
                :label="$t('Users.Form.email')"
                :error-messages="emailErrors"
                @input="$v.user.email.$touch()"
                @blur="$v.user.email.$touch()"
                prepend-inner-icon="alternate_email"
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="3">
              <v-text-field v-model="user.firstName" :label="$t('Users.Form.firstname')"></v-text-field>
            </v-col>
            <v-col cols="12" md="3">
              <v-text-field v-model="user.lastName" :label="$t('Users.Form.lastname')"></v-text-field>
            </v-col>
            <v-col cols="12" md="6">
              <MallSelector
                v-model="user.mall"
                outline
                :label="$t('Malls.Selector.label')"
                :hint="admin ? $t('Malls.Selector.hintAdmin') : $t('Malls.Selector.hintUser')"
                :error-messages="mallErrors"
                :disabled="!!(!admin && user.id)"
                :clearable="!(!admin && user.id)"
              />
            </v-col>
            <v-col cols="12" md="6" v-if="!admin">
              <DataFetcher v-slot:default="{ items }" endpoint="/malls">
                <v-select
                  v-model="user.accessDocumentsOf"
                  :items="items"
                  multiple
                  outlined
                  label="Accès aux documents de ..."
                  hint="L'enseigne pourra consulter les documents de ces centres"
                  item-text="name"
                  color="primary"
                  value="id"
                  return-object
                  persistent-hint
                >

                </v-select>
              </DataFetcher>
            </v-col>
          </v-row>
          <v-slide-y-transition>
            <v-row v-if="!admin">
              <v-col cols="12" sm="12">
                <h3 class="title">{{$t('Users.Form.informations')}}</h3>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field v-model="user.street" :label="$t('Users.Form.street')" prepend-inner-icon="mdi-map-marker"></v-text-field>
                <v-text-field v-model="user.complement" :label="$t('Users.Form.complement')" prepend-inner-icon="mdi-map-marker"></v-text-field>
                <v-text-field v-model="user.postalCode" :label="$t('Users.Form.postalCode')" prepend-inner-icon="mdi-map-marker"></v-text-field>
                <v-text-field v-model="user.city" :label="$t('Users.Form.city')" prepend-inner-icon="mdi-map-marker"></v-text-field>
                <v-text-field v-model="user.country" :label="$t('Users.Form.country')" prepend-inner-icon="mdi-map-marker"></v-text-field>
                <v-text-field v-model="user.phone" :label="$t('Users.Form.phone')" prepend-inner-icon="mdi-phone"></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field v-model.number="user.floor" :label="$t('Users.Form.floor')" type="number" prepend-inner-icon="mdi-map-search-outline" :error-messages="floorErrors"></v-text-field>
                <v-text-field v-model.number="user.area" :label="$t('Users.Form.area')" type="number" hint="en m2" prepend-inner-icon="mdi-map-search-outline" :error-messages="areaErrors"></v-text-field>
                <v-text-field v-model="user.unit" :label="$t('Users.Form.unit')" prepend-inner-icon="mdi-map-search-outline" :error-messages="unitErrors"></v-text-field>
                <v-text-field v-model="user.zone" :label="$t('Users.Form.zone')" prepend-inner-icon="mdi-map-search-outline"></v-text-field>
                <v-select v-model="user.segment" :items="segments" :label="$t('Users.Form.segment')"></v-select>
                <v-text-field v-model.number="user.fullTimeEquivalent" :label="$t('Users.Form.fullTimeEquivalent')" type="number" prepend-inner-icon="mdi-account-group" :error-messages="fullTimeEquivalentErrors"></v-text-field>
              </v-col>
              <v-col cols="12" sm="12">
                <h3 class="title">{{$t('Users.Form.configuration')}}</h3>
              </v-col>
              <v-col cols="12" md="6">
                <DateSelector v-model="user.openingDate" :label="$t('Users.Form.openingDate')" :error-messages="openingDateErrors"/>
              </v-col>
              <v-col cols="12" md="6">
                <DateSelector v-model="user.closingDate" :label="$t('Users.Form.closingDate')"/>
              </v-col>
              <v-col cols="12">
                <h4 class="subheading">{{$t('Users.Form.dataToEnter')}}</h4>
              </v-col>
              <v-col cols="12" md="6">
                <v-select
                  v-model="user.entersTtc"
                  :items="[{text: 'Saisie TTC', value: true}, {text: 'Saisie HT', value: false}]"
                ></v-select>
                <v-switch v-model="user.entersZeroRate" :label="$t('Users.Form.zeroRate')"></v-switch>
                <v-switch v-model="user.entersSuperReducedRate" :label="$t('Users.Form.superReducedRate')"></v-switch>
                <v-switch v-model="user.entersReducedRate" :label="$t('Users.Form.reducedRate')"></v-switch>
                <v-switch v-model="user.entersIntermediateRate" :label="$t('Users.Form.intermediateRate')"></v-switch>
                <v-switch v-model="user.entersNormalRate" :label="$t('Users.Form.normalRate')"></v-switch>
              </v-col>
              <v-col cols="12" md="6">
                <v-switch v-model="user.entersQuantitiesSold" :label="$t('Users.Form.quantitiesSold')"></v-switch>
                <v-switch v-model="user.entersNbEntries" :label="$t('Users.Form.nbEntries')"></v-switch>
                <v-switch v-model="user.entersNbTransactions" :label="$t('Users.Form.nbTransactions')"></v-switch>
              </v-col>
            </v-row>
          </v-slide-y-transition>
        </v-container>
      </v-form>
    </v-card-text>

    <v-card-actions>
      <v-spacer></v-spacer>
      <v-btn color="grey" text @click="cancel">{{$t('Global.Action.cancel')}}</v-btn>
      <v-btn color="primary" text @click="save">{{$t('Global.Action.save')}}</v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import { required, requiredUnless, email } from 'vuelidate/lib/validators'
import { mapState } from 'vuex'
import MallSelector from '@/components/Utils/MallSelector'
import DateSelector from '@/components/Utils/DateSelector'
import DataFetcher from '@/components/Utils/DataFetcher'
import { segments } from '@/config/fixedValues'
import FilesPathHelpersMixin from '@/mixins/FilesPathHelpersMixin'
export default {
  name: 'UserEditor',
  components: { DateSelector, MallSelector, DataFetcher },
  mixins: [FilesPathHelpersMixin],
  props: {
    defaultUser: {type: Object, required: false},
    defaultFormTitle: {type: String, required: false},
    admin: {type: Boolean, required: false}
  },
  data: () => ({
    user: null,
    logoFile: null,
    default: {
      email: '',
      firstName: '',
      lastName: '',
      roles: [],
      brand: '',
      street: '',
      complement: '',
      postalCode: '',
      city: '',
      country: '',
      phone: '',
      floor: 0,
      area: 0,
      unit: '',
      zone: '',
      segment: segments[0],
      fullTimeEquivalent: 0,
      openingDate: null,
      closingDate: null,
      counterType: 0,
      entersZeroRate: true,
      entersSuperReducedRate: true,
      entersReducedRate: true,
      entersIntermediateRate: true,
      entersNormalRate: true,
      entersTtc: true,
      entersQuantitiesSold: true,
      entersNbEntries: true,
      entersNbTransactions: true,
      mall: null,
      isActive: true,
      accessDocumentsOf: []
    },
    segments: [...segments],
  }),
  computed: {
    logoPreview () {
      if (this.logoFile) {
        return URL.createObjectURL(this.logoFile)
      } else if (this.user.picture) {
        return this.getPicturePath(this.user.picture)
      } else {
        return '/default_logo.svg'
      }
    },
    roles () {
      if (this.admin) {
        return ['ROLE_USER', 'ROLE_SUPER_ADMIN']
      }
      return ['ROLE_USER']
    },
    ...mapState('user', [
      'usersList'
    ]),
    formTitle () {
      return this.defaultFormTitle || (this.user.id ? 'Modification' : 'Création')
    },
    emailErrors () {
      const errors = []
      if (!this.$v.user.email.$dirty) return errors
      !this.$v.user.email.isUnique && errors.push(this.$t('Users.Form.emailAlreadyUsed'))
      !this.$v.user.email.email && errors.push(this.$t('Users.Form.emailInvalid'))
      !this.$v.user.email.required && errors.push(this.$t('Global.Common.required'))
      return errors
    },
    brandErrors () {
      const errors = []
      if (!this.$v.user.brand.$dirty) return errors
      !this.$v.user.brand.required && errors.push(this.$t('Global.Common.required'))
      return errors
    },
    floorErrors () {
      const errors = []
      if (!this.$v.user.floor.$dirty) return errors
      !this.$v.user.floor.required && errors.push(this.$t('Global.Common.required'))
      return errors
    },
    unitErrors () {
      const errors = []
      if (!this.$v.user.unit.$dirty) return errors
      !this.$v.user.unit.required && errors.push(this.$t('Global.Common.required'))
      return errors
    },
    areaErrors () {
      const errors = []
      if (!this.$v.user.area.$dirty) return errors
      !this.$v.user.area.required && errors.push(this.$t('Global.Common.required'))
      return errors
    },
    fullTimeEquivalentErrors () {
      const errors = []
      if (!this.$v.user.fullTimeEquivalent.$dirty) return errors
      !this.$v.user.fullTimeEquivalent.required && errors.push(this.$t('Global.Common.required'))
      return errors
    },
    openingDateErrors () {
      const errors = []
      if (!this.$v.user.openingDate.$dirty) return errors
      !this.$v.user.openingDate.required && errors.push(this.$t('Global.Common.required'))
      return errors
    },
    mallErrors () {
      const errors = []
      if (!this.$v.user.mall.$dirty) return errors
      !this.$v.user.mall.required && errors.push(this.$t('Global.Common.required'))
      return errors
    },
  },
  watch: {
    defaultUser () {
      this.resetForm()
    }
  },
  validations: {
    user: {
      email: { required, email, isUnique(value) {
        // standalone validator ideally should not assume a field is required
        if (value === '') return true

        return this.usersList.every(user => user.email.toLowerCase() !== value.toLowerCase() || user.id === this.user.id || !user.isActive || !this.user.isActive)
      }},
      brand: { required: requiredUnless(function () {return this.admin}) },
      floor: { required: requiredUnless(function () {return this.admin}) },
      unit: { required: requiredUnless(function () {return this.admin}) },
      area: { required: requiredUnless(function () {return this.admin}) },
      fullTimeEquivalent: { required: requiredUnless(function () {return this.admin}) },
      openingDate: { required: requiredUnless(function () {return this.admin}) },
      mall: { required: requiredUnless(function () {return this.admin}) }
    }
  },
  methods: {
    init () {
      this.user = this.defaultUser ? {...this.defaultUser} : {...this.default, roles: this.roles}

      // If the API has an extra segment, we add it to the available segments
      if (!this.segments.includes(this.user.segment)) {
        this.segments.push(this.user.segment)
      }
    },
    save () {
      this.$v.$touch()
      if (!this.$v.$invalid) {
        this.$emit('save', {...this.user, file: this.logoFile})
      } else {
        this.$emit('invalid-form')
      }
    },
    cancel () {
      this.$emit('cancel')
      this.resetForm()
    },
    resetForm () {
      this.init()
      this.$v.$reset()
    },
  },
  async created () {
    this.init()
  }
}
</script>
